import { IEquityIssueSignupFormData } from "@portal/types/api/IEquityIssueSignupFormData";
import { createContext, useContext, useMemo } from "react";
import { ISignupForm } from "./types";
import { ActingOnBehalfOf, IEquityIssueUserInfo } from "@portal/types/api/IEquityIssueUserInfo";
import { IEquityIssueDetails } from "@portal/types/api/IEquityIssueDetails";
import { IEquityIssueAppropriateTest } from "@portal/types/api/IEquityIssueAppropriateTest";
import { format } from "date-fns";
import usePersistState, { clearPersistedState } from "@portal/hooks/usePersistState";

const initialSignUpData = {
  id: 0,
  userInfoDto: {
    id: 0,
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    address: "",
    postalCode: "",
    city: "",
    country: "",
    nationalId: "",
    actingOnBehalfOf: ActingOnBehalfOf.Personal,
    basicInformationEditable: true,
  },
  prokuraSignature: {
    canSign: false,
    signature: "",
  },
  details: {
    acceptedDisclaimer: undefined,
    equityCount: 0,
    equityCountString: "",
    customLimit: undefined,
    customLimitString: "",
    bankAccount: undefined,
    bankAccountExplanation: undefined,
    vpsAccount: "",
    acceptedAuthorization: undefined,
    acceptedProspect: undefined,
    conditionalOrder: undefined,
  },
  created: format(new Date(), "yyyy-MM-dd"),
  updated: undefined,
  appropriateTest: {
    relevantProfession: undefined,
    relevantProfessionNumber: -1,
    profession: "",
    education: undefined,
    numberOfTradesLast12MonthsUnlistedFinancialInstruments: undefined,
    knowledgeLevelUnlistedFinancialInstruments: undefined,
    numberOfTradesLast12MonthsListedFinancialInstruments: undefined,
    knowledgeLevelListedFinancialInstruments: undefined,
    adviceAgainstSigningReceived: false,
    adviceAgainstSigningAccepted: undefined,
    adviceAgainstSigningAcceptedNumber: -1,
    appropriateTestReceived: false,
  },
} as IEquityIssueSignupFormData;

export const SignupFormContext = createContext<ISignupForm | null>(null);

export function SignupFormProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const [signUpData, setSignUpData] = usePersistState<IEquityIssueSignupFormData>("equityIssueSignUpForm", initialSignUpData);

  const signup = useMemo(() => {
    const updateSignupForm = (values: Partial<IEquityIssueSignupFormData>) => {
      setSignUpData({ ...signUpData, ...values });
    };

    const updateUserInfo = (values: Partial<IEquityIssueUserInfo>) => {
      setSignUpData({ ...signUpData, userInfoDto: { ...signUpData.userInfoDto, ...values } });
    };

    const updateDetails = (values: Partial<IEquityIssueDetails>) => {
      setSignUpData({ ...signUpData, details: { ...signUpData.details, ...values } });
    };

    const updateAppropriateTest = (values: Partial<IEquityIssueAppropriateTest>) => {
      setSignUpData({ ...signUpData, appropriateTest: { ...signUpData.appropriateTest, ...values } });
    };

    const clearSignupForm = () => {
      setSignUpData(initialSignUpData);
      clearPersistedState("equityIssueSignUpForm");
    };

    const submitForm = async (equityIssueId: string) => {
      const response = await fetch(`/api/equityissue/${equityIssueId}/signup`, {
        method: "POST",
        body: JSON.stringify(signUpData),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit form`);
      }

      const equityIssueSignupResponse = await response.json();
      return equityIssueSignupResponse;
    };

    return {
      signupForm: signUpData,
      updateSignupForm,
      updateUserInfo,
      updateDetails,
      updateAppropriateTest,
      submitForm,
      clearSignupForm,
    };
  }, [signUpData, setSignUpData]);

  return <SignupFormContext.Provider value={signup}>{children}</SignupFormContext.Provider>;
}

export const useSignupForm = (): ISignupForm => {
  const context = useContext(SignupFormContext);
  if (!context) {
    throw new Error("useSignupForm must be used within a SignupFormProvider");
  }
  return context;
};
